/**
 * Shared Swiper carousel factory.
 *
 * Provides the common custom-element scaffolding every simple carousel in the
 * theme used to duplicate: the "Swiper is not loaded" guard, the constructor,
 * the standard autoplay config, initialising Swiper on connect and destroying
 * it on disconnect (which also covers the Theme Editor's shopify:section:load —
 * the section is re-rendered, the old element is disconnected and destroyed and
 * the new one connects and re-initialises).
 *
 * Load order: swiper-bundle.min.js (deferred, in <head>) executes before this
 * file, and this file executes before each carousel's own deferred script, so
 * `Swiper` and `SwiperCarouselElement` are both defined by the time a subclass
 * calls customElements.define() and its connectedCallback runs. The guard below
 * still handles the case where Swiper somehow has not loaded.
 *
 * Subclasses provide getSwiperElement() and getSwiperOptions(); everything else
 * is inherited. The file is idempotent — it may be included by several sections
 * on the same page, so it no-ops if the base class already exists.
 */
;(function () {
  if (window.SwiperCarouselElement) return

  class SwiperCarouselElement extends HTMLElement {
    constructor() {
      super()
      this.swiper = null
    }

    connectedCallback() {
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper is not loaded')
        return
      }

      const swiperElem = this.getSwiperElement()
      if (!swiperElem) return

      this.swiper = new Swiper(swiperElem, this.getSwiperOptions(swiperElem))
    }

    disconnectedCallback() {
      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true)
      }
    }

    /**
     * Standard autoplay config read from the shared data attributes.
     * Returns undefined when autoplay is disabled so Swiper omits the module,
     * matching the original per-carousel implementations exactly.
     */
    getAutoplay() {
      if (this.dataset.allowAutoplay !== 'true') return undefined
      return {
        delay: parseInt(this.dataset.autoplayDelay || '2500', 10),
        disableOnInteraction: this.dataset.autoplayDisableOnInteraction === 'true',
      }
    }

    // Subclasses must override these two.
    getSwiperElement() {
      return null
    }

    getSwiperOptions() {
      return {}
    }
  }

  window.SwiperCarouselElement = SwiperCarouselElement
})()
